import { useCallback } from "react";
import { WatchQueryOptions, useApolloClient } from "@apollo/client";
import { AnalyticsBrowser } from "@segment/analytics-next";

import isSSR from "@/utils/isSSR";
import { useAnalytics } from "@/contexts/AnalyticsContext";
import { useExperimentBuckets } from "@/contexts/ExperimentContext";

declare global {
  interface Window {
    FS?: {
      getCurrentSessionURL?: (bool: boolean) => string;
    };
  }
}

const track = (
  analytics: AnalyticsBrowser,
  {
    method,
    props,
    additionalProps,
    event,
    segmentOpts,
    element,
    cb,
  }: {
    method: "track" | "trackLink";
    props: object;
    additionalProps: object;
    event: string;
    segmentOpts: Parameters<AnalyticsBrowser["track"]>[2];
    element: Parameters<AnalyticsBrowser["trackLink"]>[0];
    cb: Parameters<AnalyticsBrowser["track"]>[3];
  },
) => {
  // We wrap this in a ready to make sure FS is available.
  analytics.ready(() => {
    const totalProps = {
      ...props,
      ...additionalProps,
      fullstory_url: window.FS?.getCurrentSessionURL?.(true) || null,
    };

    if (method === "trackLink" && element) {
      try {
        analytics.trackLink(element, event, totalProps, segmentOpts);
      } catch (error) {
        // Segment throws an error if the trackLink call takes longer than 300ms
        // This doesn't affect the user experience, so we just log the error to prevent monitor noise
        // eslint-disable-next-line no-console
        console.warn("Segment trackLink error", error);
      }
    } else {
      analytics.track(event, totalProps, segmentOpts, cb);
    }
  });
};

const useSegmentTrack = (method: "track" | "trackLink" = "track") => {
  const analytics = useAnalytics();
  const client = useApolloClient();
  const experimentProps = useExperimentBuckets();

  /**
   * The actual hook so we can execute it outside of functional components
   *
   * @param {string} event - The name of the segment event
   * @param {string} opts - options including segment props and track event options
   * @param {Object} opts.query - graphql query that triggered the track event
   * @param {Object} opts.segmentOpts - options for the segment track function
   * @param {Object} opts.props - any non-shared props to pass to the segment event
   * @param {Object} opts.variables - an object containing the query variables
   * @param {function} opts.transformer - a function to transform the cache data to segment props
   * @param {function} cb - callback to pass to segment track function
   */
  return useCallback(
    (
      event: string,
      {
        query,
        variables,
        props: additionalProps,
        transformer,
        segmentOpts,
        element,
      }: {
        query?: WatchQueryOptions["query"];
        variables?: WatchQueryOptions["variables"];
        props?: object;
        transformer?: <TData, TVariables>(
          data: TData,
          variables: TVariables,
        ) => object;
        segmentOpts?: Parameters<AnalyticsBrowser["track"]>[2];
        element?: Parameters<AnalyticsBrowser["trackLink"]>[0];
      } = {},
      cb?: Parameters<AnalyticsBrowser["track"]>[3],
    ) => {
      if (!event || typeof event !== "string" || isSSR()) {
        return;
      }

      if (query && variables?.id) {
        let resData;
        const subscription = client
          .watchQuery({
            query,
            variables,
            errorPolicy: "all",
            fetchPolicy: "cache-only",
          })
          .subscribe({
            next: ({ data }) => {
              resData = data;
              const transformedProps = transformer
                ? transformer(data, variables)
                : {};
              track(analytics, {
                method,
                props: { ...experimentProps, ...transformedProps },
                additionalProps: additionalProps || {},
                event,
                segmentOpts,
                element: element || null,
                cb,
              });
              if (resData) {
                subscription.unsubscribe();
              }
            },
          });
      } else {
        track(analytics, {
          method,
          props: experimentProps,
          additionalProps: additionalProps || {},
          event,
          segmentOpts,
          element: element || null,
          cb,
        });
      }
    },
    [analytics, client, method, experimentProps],
  );
};

export default useSegmentTrack;
